<template>
  <div class="home">
    <!-- Hero section -->
    <section class="bg-gradient-to-b from-white to-orange-50 py-24 relative overflow-hidden">
      <div class="container mx-auto px-4">
        <div class="flex flex-wrap items-center">
          <div class="w-full lg:w-1/2 mb-10 lg:mb-0 relative z-10">
            <span class="bg-orange-100 text-primary font-bold text-xs uppercase tracking-wider py-2 px-4 rounded-full inline-block mb-3">La mejor plataforma para autos chocados</span>
            <h1 class="text-5xl font-extrabold leading-tight text-gray-800 mb-4 tracking-tight">
              RÁPIDO, FÁCIL <span class="text-primary">Y SEGURO</span>
            </h1>
            <p class="text-lg text-gray-600 leading-relaxed mb-6 max-w-lg">
              Vender tu vehículo chocado, nunca fue tan fácil
            </p>
            <div class="flex flex-wrap gap-4 mb-12">
              <!-- Para usuarios no autenticados o compradores: Explorar Vehículos -->
              <router-link v-if="!isAuthenticated || isBuyer" to="/vehicles" class="btn-primary rounded-full px-6 py-3 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Explorar Vehículos
              </router-link>
              <!-- Para vendedores: Publicar un Vehículo -->
              <router-link v-if="isAuthenticated && isSeller" to="/my-vehicles" class="btn-primary rounded-full px-6 py-3 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Publicar un Vehículo
              </router-link>
              <!-- Botón de registro solo para usuarios no autenticados -->
              <router-link v-if="!isAuthenticated" to="/register" class="border border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white transition duration-200 rounded-full px-6 py-3 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Registrate
              </router-link>
            </div>
          </div>
          <div class="w-full lg:w-1/2 hidden lg:block relative">
            <div class="relative">
              <div class="w-[500px] h-[400px] ">
                <img :src="ornamentImage" alt="Ornamento decorativo de Full Chocados" class="w-full h-full object-cover">
              </div>
              <div class="absolute bottom-10 right-[-30px] bg-white p-4 rounded-xl shadow-lg max-w-[250px] z-10 animate-float">
                <div class="flex items-center mb-2">
                  <div class="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold mr-2">JD</div>
                  <span class="font-bold">Juan D.</span>
                </div>
                <div class="text-green-600 mb-1 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Oferta aceptada
                </div>
                <small>"¡Vendiste tu auto chocado!"</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How it Works Section - Para Compradores -->
    <section v-if="!isAuthenticated || isBuyer" class="py-20">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-12">¿Cómo Funciona para Compradores?</h2>
        <div class="flex flex-wrap -mx-4">
          <div class="w-full md:w-1/3 px-4 mb-10 md:mb-0">
            <div class="text-center">
              <div class="w-20 h-20 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 class="text-2xl font-bold mb-3">Regístrate</h3>
              <p class="text-gray-600">Crea tu cuenta como comprador para acceder al catálogo completo de vehículos.</p>
            </div>
          </div>
          <div class="w-full md:w-1/3 px-4 mb-10 md:mb-0">
            <div class="text-center">
              <div class="w-20 h-20 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 class="text-2xl font-bold mb-3">Carga tu Seña</h3>
              <p class="text-gray-600">Realiza un depósito de ARS 200.000 que se utilizará como parte del pago al ofertar.</p>
            </div>
          </div>
          <div class="w-full md:w-1/3 px-4">
            <div class="text-center">
              <div class="w-20 h-20 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 class="text-2xl font-bold mb-3">Oferta y Negocia</h3>
              <p class="text-gray-600">Explora vehículos, realiza ofertas y negocia directamente con los vendedores.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How it Works Section - Para Vendedores -->
    <section v-if="isAuthenticated && isSeller" class="py-20">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-12">¿Cómo Funciona para Vendedores?</h2>
        <div class="flex flex-wrap -mx-4">
          <div class="w-full md:w-1/3 px-4 mb-10 md:mb-0">
            <div class="text-center">
              <div class="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 class="text-2xl font-bold mb-3">Publica tu Vehículo</h3>
              <p class="text-gray-600">Sube fotos, describe el estado y daños. Establece tu precio inicial de venta.</p>
            </div>
          </div>
          <div class="w-full md:w-1/3 px-4 mb-10 md:mb-0">
            <div class="text-center">
              <div class="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5zM4.828 7l6.586 6.586a2 2 0 002.828 0l6.586-6.586a2 2 0 000-2.828L14.242.758a2 2 0 00-2.828 0L4.828 4.172a2 2 0 000 2.828z" />
                </svg>
              </div>
              <h3 class="text-2xl font-bold mb-3">Recibe Ofertas</h3>
              <p class="text-gray-600">Los compradores interesados te enviarán ofertas que puedes aceptar, rechazar o negociar.</p>
            </div>
          </div>
          <div class="w-full md:w-1/3 px-4">
            <div class="text-center">
              <div class="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 class="text-2xl font-bold mb-3">Vende Seguro</h3>
              <p class="text-gray-600">Una vez acordado el precio, la transacción se realiza de forma segura y protegida.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured Vehicles Section - Solo visible para compradores -->
    <section v-if="isAuthenticated && isBuyer" class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="flex justify-between items-center mb-8">
          <h2 class="text-3xl font-bold">Vehículos Destacados</h2>
          <router-link to="/vehicles" class="border border-primary text-primary hover:bg-primary hover:text-white transition duration-200 px-4 py-2 rounded-lg font-medium">
            Ver Todos
          </router-link>
        </div>
        
        <div v-if="loading" class="text-center py-16">
          <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p class="text-gray-600">Cargando vehículos...</p>
        </div>
        
        <div v-else-if="vehicles.length === 0" class="text-center py-16">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p class="text-gray-600">No hay vehículos disponibles en este momento.</p>
        </div>
        
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="vehicle in featuredVehicles" :key="vehicle.id" class="bg-white rounded-xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-200">
            <div class="relative">
              <img 
                v-if="vehicle.primary_image" 
                :src="vehicle.primary_image.image" 
                :alt="vehicle.title"
                class="w-full h-48 object-cover"
              />
              <img 
                v-else 
                src="https://via.placeholder.com/300x200?text=Sin+Imagen" 
                alt="Sin imagen"
                class="w-full h-48 object-cover"
              />
              
              <!-- Tag de Vehículo Destacado -->
              <div v-if="vehicle.is_featured" class="absolute top-2 left-2">
                <span class="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-bold uppercase tracking-wider py-1 px-2 rounded-md shadow-lg">
                  ⭐ Destacado
                </span>
              </div>
              
              <!-- Tag de nivel de daño -->
              <div class="absolute top-2 right-2">
                <span class="bg-red-500 text-white text-xs font-bold uppercase tracking-wider py-1 px-2 rounded-md">
                  {{ vehicle.damage_level_display }}
                </span>
              </div>
            </div>
            
            <div class="p-5">
              <h3 class="text-xl font-bold mb-1">{{ vehicle.brand }} {{ vehicle.model }}</h3>
              <p class="text-gray-600 text-sm mb-3">{{ vehicle.year }}</p>
              <div class="flex justify-between items-center mb-4">
                <!-- Mostrar precio solo si el usuario está autenticado -->
                <span v-if="isAuthenticated" class="text-xl font-bold text-primary">ARS {{ formatPrice(vehicle.price) }}</span>
                <span v-else class="text-sm italic text-gray-600">Inicia sesión para ver el precio</span>
                <span :class="`text-xs font-bold px-2 py-1 rounded-full ${getStatusClass(vehicle.status)}`">
                  {{ vehicle.status_display }}
                </span>
              </div>
              <router-link :to="'/vehicles/' + vehicle.id" class="block text-center bg-primary hover:bg-primary-600 text-white font-medium py-2 rounded-lg transition duration-200">
                Ver Detalles
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Call to Action for Sellers -->
    <section v-if="isAuthenticated && isSeller" class="py-16 bg-gradient-to-r from-green-600 to-green-700">
      <div class="container mx-auto px-4 text-center">
        <div class="max-w-3xl mx-auto">
          <h2 class="text-4xl font-bold text-white mb-6">
            ¡Publica tu Vehículo Chocado!
          </h2>
          <p class="text-xl text-green-100 mb-8">
            Conecta con compradores interesados y vende tu vehículo de forma rápida y segura. Miles de usuarios buscan vehículos como el tuyo.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link 
              to="/add-vehicle" 
              class="bg-white text-green-600 hover:bg-gray-50 font-bold py-4 px-8 rounded-lg transition duration-200 text-lg flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Publicar Vehículo
            </router-link>
            <router-link 
              to="/my-vehicles" 
              class="border-2 border-white text-white hover:bg-white hover:text-green-600 font-bold py-4 px-8 rounded-lg transition duration-200 text-lg flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Ver Mis Vehículos
            </router-link>
          </div>
          <div class="mt-8 text-green-100">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div class="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm">Sin comisiones ocultas</span>
              </div>
              <div class="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span class="text-sm">Transacciones seguras</span>
              </div>
              <div class="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span class="text-sm">Venta rápida</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Call to Action for Non-Authenticated Users -->
    <section v-if="!isAuthenticated" class="py-16 bg-gradient-to-r from-primary to-primary-600">
      <div class="container mx-auto px-4 text-center">
        <div class="max-w-3xl mx-auto">
          <h2 class="text-4xl font-bold text-white mb-6">
            ¡Accede a los Mejores Vehículos!
          </h2>
          <p class="text-xl text-blue-100 mb-8">
            Regístrate o inicia sesión para explorar nuestro catálogo completo de vehículos chocados con precios increíbles.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link 
              to="/register" 
              class="bg-white text-primary hover:bg-gray-50 font-bold py-4 px-8 rounded-lg transition duration-200 text-lg"
            >
              Crear Cuenta
            </router-link>
            <router-link 
              to="/login" 
              class="border-2 border-white text-white hover:bg-white hover:text-primary font-bold py-4 px-8 rounded-lg transition duration-200 text-lg"
            >
              Iniciar Sesión
            </router-link>
          </div>
          <div class="mt-8 text-blue-100">
            <p class="text-sm">
              🔒 Solo usuarios registrados pueden ver vehículos y realizar ofertas
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Advantages Section -->
    <section class="py-20">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-12">¿Por qué Full Chocados?</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div class="text-center">
            <div class="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h4 class="text-xl font-bold mb-2">Transacciones Seguras</h4>
            <p class="text-gray-600">Sistema de negociaciones con protección para compradores y vendedores.</p>
          </div>
          
          <div class="text-center">
            <div class="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h4 class="text-xl font-bold mb-2">Comunicación Directa</h4>
            <p class="text-gray-600">Negociación directa sin compartir datos personales.</p>
          </div>
          
          <div class="text-center">
            <div class="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h4 class="text-xl font-bold mb-2">Ofertas Transparentes</h4>
            <p class="text-gray-600">Todas las ofertas y contraofertas quedan registradas en el sistema.</p>
          </div>
          
          <div class="text-center">
            <div class="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h4 class="text-xl font-bold mb-2">Soporte Completo</h4>
            <p class="text-gray-600">Equipo de soporte disponible durante todo el proceso.</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ornamentImage from '@/assets/images/ornament.png'

export default {
  name: 'Home',
  data() {
    return {
      loading: true,
      ornamentImage: ornamentImage
    }
  },
  computed: {
    ...mapState({
      vehicles: state => state.vehicles
    }),
    ...mapGetters([
      'isAuthenticated',
      'isBuyer',
      'isSeller'
    ]),
    featuredVehicles() {
      // Defensively check if vehicles is an array before filtering.
      if (!Array.isArray(this.vehicles)) {
        return [];
      }
      // Return up to 3 featured vehicles
      return this.vehicles.filter(v => v.status === 'active').slice(0, 3);
    }
  },
  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat('es-AR').format(price)
    },
    getStatusVariant(status) {
      const variants = {
        active: 'success',
        sold: 'danger',
        reserved: 'warning',
        inactive: 'secondary'
      }
      return variants[status] || 'secondary'
    },
    getStatusClass(status) {
      const classes = {
        active: 'bg-green-100 text-green-800',
        sold: 'bg-red-100 text-red-800',
        reserved: 'bg-yellow-100 text-yellow-800',
        inactive: 'bg-gray-100 text-gray-800'
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    }
  },
  async created() {
    try {
      // Solo cargar vehículos si el usuario está autenticado y es comprador
      if (this.isAuthenticated && this.isBuyer) {
        await this.$store.dispatch('fetchVehicles')
      }
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
/* Animación de flotación para la tarjeta de testimonio */
@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>
